import { useSearchParams, useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "../api/axios";
import { toast } from "react-toastify";
import PasswordStrengthBar from "react-password-strength-bar";
import * as React from "react";
import { motion } from "framer-motion";
import { Lock, Save, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [passwordTouched, setPasswordTouched] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (!token) {
            setError("O link de redefinição não contém um token válido.");
            return;
        }
        if (!isPasswordValid(password)) {
            setError("A senha não atende aos requisitos mínimos de segurança.");
            return;
        }
        setLoading(true);
        try {
            await axios.post("/auth/reset-password", {
                token,
                newPassword: password
            });
            toast.success("Senha redefinida com sucesso!");
            navigate("/");
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || "Erro ao redefinir senha. Verifique se o link ainda é válido.";
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordTouched(true);
        setPassword(e.target.value);
    };

    const isPasswordValid = (password: string): boolean => {
        return (
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /\d/.test(password) &&
            /[^A-Za-z0-9]/.test(password)
        );
    };

    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[^A-Za-z0-9]/.test(password);

    return (
        <div className="auth-shell">
            <div className="pointer-events-none absolute -left-20 -top-20 h-80 w-80 rounded-full bg-blue-600/10 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-cyan-500/5 blur-[100px]" />
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="auth-card"
            >
                <div className="flex flex-col items-center mb-8">
                    <img
                        src="/prefeitura_logo.png"
                        alt="Prefeitura Municipal Logo"
                        className="w-20 h-20 mb-4"
                    />
                    <h2 className="app-page-title text-center text-3xl font-black">
                        Redefinir Senha
                    </h2>
                    <p className="text-slate-500 mt-2 text-center">
                        Insira sua nova senha abaixo.
                    </p>
                </div>

                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="feedback-error mb-6 flex items-start gap-2"
                    >
                        <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-slate-300 font-medium mb-1 sr-only">Nova senha</label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                                <Lock size={20} />
                            </span>
                            <input
                                type="password"
                                name="password"
                                value={password}
                                onChange={handleChange}
                                className="auth-input pl-10"
                                placeholder="Nova Senha"
                                required
                            />
                        </div>
                    </div>

                    {passwordTouched && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="space-y-2 text-sm text-slate-300 bg-slate-950 p-4 rounded-lg"
                        >
                            <p className="font-semibold text-white mb-1">A senha deve conter:</p>
                            <div className="flex items-center gap-2">
                                {hasMinLength ? <CheckCircle size={18} className="text-green-500" /> : <XCircle size={18} className="text-red-500" />}
                                <span>Pelo menos 8 caracteres</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasUppercase ? <CheckCircle size={18} className="text-green-500" /> : <XCircle size={18} className="text-red-500" />}
                                <span>Pelo menos uma letra maiúscula</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasNumber ? <CheckCircle size={18} className="text-green-500" /> : <XCircle size={18} className="text-red-500" />}
                                <span>Pelo menos um número</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasSpecialChar ? <CheckCircle size={18} className="text-green-500" /> : <XCircle size={18} className="text-red-500" />}
                                <span>Pelo menos um caractere especial</span>
                            </div>
                        </motion.div>
                    )}

                    {passwordTouched && password && (
                        <div className="mt-2">
                            <PasswordStrengthBar
                                password={password}
                                scoreWords={["Muito Fraca", "Fraca", "Ok", "Boa", "Forte"]}
                                shortScoreWord="Muito Curta"
                                className="password-strength-bar"
                            />
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={loading || !isPasswordValid(password)}
                            className="ui-button-primary w-full py-4"
                        >
                            {loading ? (
                                "CARREGANDO"
                            ) : (
                                <>
                                    <Save size={20} />
                                    Redefinir senha
                                </>
                            )}
                        </button>
                    </div>
                </form>

                <div className="text-center mt-8 text-slate-500 text-sm">
                    © <a href="https://portosoftware.com.br/" target="_blank" rel="noreferrer" className="hover:text-blue-600 transition-colors duration-200">
                    {new Date().getFullYear()} PortoSoftware
                </a>
                </div>
            </motion.div>
        </div>
    );
}
